/**
 * Vertical event trail for a run. Events are derived from the run object —
 * no extra API call. See spec § "Component breakdown — RunTimeline" in
 * docs/superpowers/specs/2026-05-07-run-detail-polish-m35-design.md.
 */
import type { RunResponse } from "@simpliwork/api-schema";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatTimestamp } from "@/lib/formatters";

type TimelineEvent = {
  key: string;
  label: string;
  at: string | null;
  kind: "queued" | "started" | "tool" | "completed" | "failed" | "running";
};

const KIND_DOT_CLASS: Record<TimelineEvent["kind"], string> = {
  queued: "bg-gray-300",
  started: "bg-blue-500",
  tool: "bg-amber-500",
  completed: "bg-green-500",
  failed: "bg-red-500",
  running: "bg-blue-500 animate-pulse",
};

function deriveEvents(run: RunResponse): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  events.push({ key: "queued", label: "Queued", at: run.created_at, kind: "queued" });
  if (run.started_at) {
    events.push({ key: "started", label: "Started", at: run.started_at, kind: "started" });
  }

  const content = (run.output as { message?: { content?: unknown[] } } | null)?.message?.content;
  if (Array.isArray(content)) {
    content.forEach((block, idx) => {
      if (
        block &&
        typeof block === "object" &&
        (block as { type?: string }).type === "tool_use"
      ) {
        const name = (block as { name?: string }).name ?? "unknown tool";
        events.push({
          key: `tool-${idx}`,
          label: `Called ${name}`,
          at: run.started_at,
          kind: "tool",
        });
      }
    });
  }

  if (run.status === "running" || run.status === "pending") {
    events.push({ key: "running", label: "Running…", at: null, kind: "running" });
  } else if (run.error) {
    events.push({ key: "failed", label: "Failed", at: run.completed_at, kind: "failed" });
  } else if (run.completed_at) {
    events.push({
      key: "completed",
      label: "Completed",
      at: run.completed_at,
      kind: "completed",
    });
  }

  return events;
}

export function RunTimeline({ run }: { run: RunResponse }) {
  const events = deriveEvents(run);
  return (
    <Card className="border-gray-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-gray-700">Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="flex flex-col gap-2.5">
          {events.map((event) => (
            <li key={event.key} className="flex items-start gap-3">
              <span
                className={`mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full ${KIND_DOT_CLASS[event.kind]}`}
                aria-hidden="true"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900">{event.label}</p>
                {event.at && (
                  <p
                    className="text-xs text-muted-foreground"
                    title={new Date(event.at).toISOString()}
                  >
                    {formatTimestamp(event.at)}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
